/**
 * lex.ivoliq Landing (Juni 2026) — Conversion-Seite mit Early-Access-Warteliste.
 * Zielgruppe: EINZELNE Rechtsanwälte in Österreich (keine Kanzleien als Organisation).
 * Landing-only-Modus (Prod-Lead-Start): Login/Registrierung ausgeblendet,
 * solange REACT_APP_LEX_ENABLED=false — Warteliste funktioniert immer.
 * Styleguide: /app/design_guidelines_lex.json ("Neo-Classic Tech").
 */
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { FileText, ShieldCheck, CalendarClock, Landmark, TrendingUp, UserCheck, Check, X } from "lucide-react";
import { toast } from "sonner";
import { isLexEnabled } from "@/utils/lexDomain";
import { trackEvent } from "@/lib/analytics";
import "@/styles/lex.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEPS = [
  { nr: "01", title: "5 Minuten Onboarding", text: "Ihre Stammdaten, Steuernummer und Honorarlogik — einmalig erfasst. Danach fassen Sie keine Buchhaltung mehr an." },
  { nr: "02", title: "Ihre Steuerberatung übernimmt", text: "Eine echte Steuerberatungs-Kanzlei prüft, meldet und reicht für Sie ein: UVA, Pauschalierung, Jahreserklärung — alles inklusive." },
  { nr: "03", title: "Sie bestätigen nur", text: "Ihre monatliche Honorarnote entsteht automatisch. Fristen, Grenzen und Meldungen werden für Sie überwacht." },
];

const OUTCOMES = [
  { icon: CalendarClock, title: "Null Minuten Buchhaltung im Monat", text: "Honorarnote, UVA und Steuererklärung entstehen ohne Ihr Zutun. Sie bestätigen mit einem Klick — das war's." },
  { icon: ShieldCheck, title: "Keine Frist mehr im Kopf", text: "UVA-Termine, Vorauszahlungen, Grenzen und Quotenregelung werden überwacht — Sie werden gewarnt, bevor es kritisch wird." },
  { icon: UserCheck, title: "Eine echte Kanzlei, kein Chatbot", text: "Ihre Buchhaltung wird von einer persönlichen Steuerberatung geführt, geprüft und eingereicht." },
];

const NEVER_AGAIN = [
  "Abends Honorarnoten schreiben und Umsatzsteuer nachrechnen",
  "An UVA-Fristen denken — oder sie verpassen",
  "Belege und Unterlagen für den Jahresabschluss zusammensuchen",
  "Kleinunternehmer- und Pauschalierungsgrenzen selbst überwachen",
  "Buchhaltungssoftware bedienen, die für Buchhalter gebaut wurde",
];

const FEATURES = [
  { icon: FileText, title: "Ihre Honorarnote schreibt sich selbst", text: "Monatlich, korrekt mit 20 % USt, an die Kanzlei, für die Sie tätig sind — ohne einen einzigen Klick." },
  { icon: ShieldCheck, title: "Die UVA geht raus — ohne Sie", text: "Istbesteuerung und Vorsteuerpauschale 1,8 % (§ 14 UStG) automatisch aktiviert. Meldung durch die Steuerberatung." },
  { icon: Landmark, title: "Die Steuererklärung ist einfach fertig", text: "E1, E1a & U1 mit Basispauschalierung § 17 EStG, SVS und Grundfreibetrag korrekt angesetzt — als FinanzOnline-fertige XML." },
  { icon: TrendingUp, title: "Gewarnt, bevor es teuer wird", text: "55.000 € Kleinunternehmer, 100.000 € UVA-Rhythmus, 420.000 € Pauschalierung — mit Alarm, bevor es kritisch wird." },
  { icon: CalendarClock, title: "Kein Fristenstress", text: "UVA-Termine, ESt-Vorauszahlungen und die Quotenregelung Ihrer Steuerberatung im Fristenkalender." },
  { icon: UserCheck, title: "Persönlich betreut statt Hotline", text: "Kein Chatbot, kein Ticket-System: Ihre Buchhaltung wird von einer echten Steuerberatungs-Kanzlei geführt." },
];

function WaitlistForm() {
  const [form, setForm] = useState({ name: "", email: "", website: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/lex/waitlist`, form);
      trackEvent("lex_waitlist_signup");
      setDone(true);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Eintragung fehlgeschlagen. Bitte E-Mail prüfen und erneut versuchen.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="lex-card p-10 text-center lex-fade-up" data-testid="lex-waitlist-success">
        <div className="mx-auto w-12 h-12 flex items-center justify-center" style={{ background: "#0A1128" }}>
          <Check className="w-6 h-6" style={{ color: "#34d399" }} />
        </div>
        <h3 className="lex-serif text-2xl font-semibold mt-5" style={{ color: "#0A1128" }}>Sie stehen auf der Liste.</h3>
        <p className="text-sm text-slate-600 mt-3 max-w-sm mx-auto">
          Sie erhalten in Kürze eine Bestätigung per E-Mail. Wir melden uns persönlich bei Ihnen,
          sobald lex.ivoliq für Sie bereitsteht — mit bevorzugtem Zugang.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="lex-card p-8 sm:p-10 space-y-4" data-testid="lex-waitlist-form">
      <div className="lex-gold-line mb-2" />
      <h3 className="lex-serif text-2xl sm:text-3xl font-semibold" style={{ color: "#0A1128" }}>Frühen Zugang sichern.</h3>
      <p className="text-sm text-slate-500 pb-2">
        lex.ivoliq startet in Kürze für eine begrenzte Zahl österreichischer Rechtsanwälte.
        Unverbindlich eintragen — wir kontaktieren Sie persönlich zum Start.
      </p>
      <div>
        <label className="block text-xs uppercase tracking-widest font-semibold text-slate-500 mb-1.5">Name *</label>
        <input className="lex-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
          placeholder="Dr. Max Mustermann" required data-testid="lex-waitlist-name" />
      </div>
      <div>
        <label className="block text-xs uppercase tracking-widest font-semibold text-slate-500 mb-1.5">E-Mail *</label>
        <input type="email" className="lex-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
          placeholder="max.mustermann@beispiel.at" required data-testid="lex-waitlist-email" />
      </div>
      {/* Honeypot — für Menschen unsichtbar */}
      <input type="text" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })}
        tabIndex="-1" autoComplete="off" aria-hidden="true"
        style={{ position: "absolute", left: "-9999px", height: 0, width: 0, opacity: 0 }} />
      <button type="submit" className="lex-btn w-full !mt-6" disabled={loading} data-testid="lex-waitlist-submit"
        style={{ background: "#C5A059", color: "#0A1128" }}>
        {loading ? "Bitte warten …" : "Auf die Warteliste"}
      </button>
      <p className="text-[11px] text-slate-400 pt-1">
        Mit dem Absenden stimmen Sie zu, dass wir Sie zum Start von lex.ivoliq kontaktieren.
        Details: <Link to="/lex-datenschutz" className="underline" target="_blank">Datenschutzerklärung</Link>.
      </p>
    </form>
  );
}

export default function LexLanding() {
  const showLogin = isLexEnabled();
  const scrollToForm = () => document.getElementById("early-access")?.scrollIntoView({ behavior: "smooth" });

  useEffect(() => {
    document.title = "Ihre Steuern. Unser Mandat. | lex.ivoliq für Rechtsanwälte in Österreich";
    const desc = document.querySelector('meta[name="description"]');
    if (desc) desc.setAttribute("content", "Honorarnoten, UVA und Steuererklärung für Rechtsanwälte in Österreich — vollständig erledigt von Ihrer persönlichen Steuerberatung. Jetzt frühen Zugang sichern.");
  }, []);

  return (
    <div className="lex-theme min-h-screen" data-testid="lex-landing">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 border-b" style={{ background: "rgba(10,17,40,0.97)", borderColor: "rgba(197,160,89,0.25)" }}>
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <span className="lex-serif text-2xl font-semibold text-white">lex<span style={{ color: "#C5A059" }}>.</span>ivoliq</span>
          <div className="flex items-center gap-6">
            {showLogin && (
              <Link to="/lex-login" className="text-xs uppercase tracking-widest font-semibold text-slate-300 hover:text-white transition-colors"
                data-testid="lex-nav-login-link">Anmelden</Link>
            )}
            <button onClick={scrollToForm} className="lex-btn !py-2.5 !px-5" style={{ background: "#C5A059", color: "#0A1128" }}
              data-testid="lex-nav-cta">Früher Zugang</button>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <header className="relative overflow-hidden" style={{ background: "#0A1128" }}>
        <div className="max-w-6xl mx-auto px-6 py-24 sm:py-32">
          <div className="max-w-3xl">
            <p className="lex-mono text-xs uppercase tracking-[0.25em] lex-fade-up" style={{ color: "#C5A059" }}>
              Für Rechtsanwälte in Österreich
            </p>
            <h1 className="lex-serif text-4xl sm:text-5xl lg:text-6xl font-semibold text-white leading-[1.1] mt-6 lex-fade-up-1">
              Ihre Steuern.<br />
              <span style={{ color: "#C5A059" }}>Unser Mandat.</span>
            </h1>
            <p className="text-slate-300 mt-8 max-w-xl text-base md:text-lg lex-fade-up-2">
              Honorarnoten, Umsatzsteuervoranmeldung und Steuererklärung — vollständig erledigt
              von Ihrer persönlichen Steuerberatung. Sie bestätigen nur.
            </p>
            <div className="flex flex-wrap items-center gap-4 mt-10 lex-fade-up-3">
              <button onClick={scrollToForm} className="lex-btn !px-8 !py-4" style={{ background: "#C5A059", color: "#0A1128" }}
                data-testid="lex-hero-cta">Frühen Zugang sichern</button>
              {showLogin && (
                <Link to="/lex-login" className="lex-btn-outline !px-8 !py-4 !text-white !border-white/40 hover:!bg-white hover:!text-[#0A1128]"
                  data-testid="lex-hero-login-link">Anmelden</Link>
              )}
            </div>
            <div className="mt-14 lex-fade-up-3">
              <p className="lex-mono text-[11px] uppercase tracking-[0.2em] text-slate-500 mb-4">
                lex.ivoliq spricht FinanzOnline und Paragraphen — damit Sie es nicht müssen:
              </p>
              <div className="flex flex-wrap gap-x-8 gap-y-3">
                {["Vorsteuerpauschale 1,8 % · § 14 UStG", "Basispauschalierung · § 17 EStG", "FinanzOnline-fertige XML"].map((t) => (
                  <span key={t} className="lex-mono text-xs text-slate-400 flex items-center gap-2">
                    <span style={{ color: "#C5A059" }}>—</span>{t}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Outcomes — was sich ab Tag 1 ändert */}
      <section className="max-w-6xl mx-auto px-6 py-20 sm:py-28" id="vorteile" data-testid="lex-outcomes-section">
        <div className="lex-gold-line mb-6" />
        <h2 className="lex-serif text-3xl sm:text-4xl font-semibold" style={{ color: "#0A1128" }}>
          Was sich für Sie ändert. Ab dem ersten Monat.
        </h2>
        <div className="grid md:grid-cols-3 gap-8 mt-12">
          {OUTCOMES.map(({ icon: Icon, title, text }) => (
            <div key={title} className="lex-card p-8" data-testid="lex-outcome-card">
              <Icon className="w-5 h-5" style={{ color: "#C5A059" }} />
              <h3 className="lex-serif text-xl font-semibold mt-4" style={{ color: "#0A1128" }}>{title}</h3>
              <p className="text-sm text-slate-600 mt-3 leading-relaxed">{text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Was Sie nie wieder tun müssen */}
      <section className="py-20 sm:py-28" style={{ background: "#0A1128" }} data-testid="lex-never-again-section">
        <div className="max-w-6xl mx-auto px-6 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="lex-gold-line mb-6" />
            <h2 className="lex-serif text-3xl sm:text-4xl font-semibold text-white leading-tight">
              Was Sie nie wieder<br />tun müssen.
            </h2>
            <p className="text-slate-400 mt-6 max-w-md leading-relaxed text-base">
              Sie führen Ihre Mandate. <span className="text-white font-medium">Wir führen Ihre Steuern.</span>
            </p>
          </div>
          <ul className="space-y-4">
            {NEVER_AGAIN.map((item) => (
              <li key={item} className="flex items-start gap-3.5" data-testid="lex-never-again-item">
                <span className="flex h-7 w-7 items-center justify-center shrink-0 mt-0.5 border" style={{ borderColor: "rgba(197,160,89,0.35)" }}>
                  <X className="w-3.5 h-3.5" style={{ color: "#C5A059" }} />
                </span>
                <span className="text-slate-400 line-through leading-relaxed" style={{ textDecorationColor: "rgba(148,163,184,0.4)" }}>{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Zero-Effort — 3 Schritte */}
      <section className="max-w-6xl mx-auto px-6 py-20 sm:py-28" id="so-funktionierts">
        <div className="lex-gold-line mb-6" />
        <h2 className="lex-serif text-3xl sm:text-4xl font-semibold" style={{ color: "#0A1128" }}>
          Zero-Effort. Wörtlich gemeint.
        </h2>
        <div className="grid md:grid-cols-3 gap-8 mt-12">
          {STEPS.map((s) => (
            <div key={s.nr} className="lex-card p-8" data-testid={`lex-step-${s.nr}`}>
              <span className="lex-mono text-sm font-semibold" style={{ color: "#C5A059" }}>{s.nr}</span>
              <h3 className="lex-serif text-xl font-semibold mt-4" style={{ color: "#0A1128" }}>{s.title}</h3>
              <p className="text-sm text-slate-600 mt-3 leading-relaxed">{s.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Leistungen */}
      <section className="py-20 sm:py-28" style={{ background: "#0A1128" }} id="leistungen">
        <div className="max-w-6xl mx-auto px-6">
          <div className="lex-gold-line mb-6" />
          <h2 className="lex-serif text-3xl sm:text-4xl font-semibold text-white">
            Alles, was Sie als Anwalt steuerlich brauchen.
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-12">
            {FEATURES.map(({ icon: Icon, title, text }) => (
              <div key={title} className="p-7 border transition-colors hover:border-[#C5A059]"
                style={{ borderColor: "rgba(197,160,89,0.2)", background: "rgba(255,255,255,0.02)" }}>
                <Icon className="w-5 h-5" style={{ color: "#C5A059" }} />
                <h3 className="text-white font-semibold mt-4 text-base">{title}</h3>
                <p className="text-sm text-slate-400 mt-2 leading-relaxed">{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Early Access */}
      <section className="max-w-6xl mx-auto px-6 py-20 sm:py-28" id="early-access">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="lex-gold-line mb-6" />
            <h2 className="lex-serif text-3xl sm:text-4xl font-semibold leading-tight" style={{ color: "#0A1128" }}>
              Der Start ist limitiert.<br />Sichern Sie sich Ihren Platz.
            </h2>
            <p className="text-slate-600 mt-6 max-w-md leading-relaxed text-base">
              Damit jeder Anwalt persönlich betreut werden kann, nehmen wir zum Start nur eine
              begrenzte Zahl an Mandaten an. Wartelisten-Einträge erhalten bevorzugten Zugang.
            </p>
            <div className="mt-8 space-y-3">
              {["Unverbindlich — keine Zahlungsdaten nötig", "Bevorzugter Zugang zum Start", "Persönliche Kontaktaufnahme durch die Steuerberatung"].map((t) => (
                <div key={t} className="flex items-center gap-3 text-sm text-slate-700">
                  <Check className="w-4 h-4 shrink-0" style={{ color: "#059669" }} />{t}
                </div>
              ))}
            </div>
          </div>
          <WaitlistForm />
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t" style={{ background: "#0A1128", borderColor: "rgba(197,160,89,0.25)" }}>
        <div className="max-w-6xl mx-auto px-6 py-10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex flex-col items-center sm:items-start gap-1">
            <span className="lex-serif text-xl font-semibold text-white">lex<span style={{ color: "#C5A059" }}>.</span>ivoliq</span>
            <span className="text-xs text-slate-500" data-testid="lex-brand-bridge">
              ein Produkt von <span className="font-bold text-slate-300">ivoliq<span style={{ color: "#34d399" }}>.</span></span>
            </span>
          </div>
          <p className="lex-mono text-xs text-slate-500">
            ivoliq GmbH · ATU83347308
            {showLogin && <> · <Link to="/kanzlei" className="underline hover:text-slate-300" data-testid="lex-kanzlei-login-link">Kanzlei-Login</Link></>}{" "}
            · <Link to="/lex-agb" className="underline hover:text-slate-300">AGB</Link>{" "}
            · <Link to="/lex-datenschutz" className="underline hover:text-slate-300">Datenschutz</Link>{" "}
            · <Link to="/lex-impressum" className="underline hover:text-slate-300">Impressum</Link>
          </p>
        </div>
      </footer>
    </div>
  );
}
